import {
  type DefaultError,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryReturnType
} from '@tanstack/vue-query';
import type {
  IAttributesResponse,
  IAttributesUpdateRequest,
  IDetailsResponse,
  IDetailsUpdateRequest
} from '@/types/services/user.service';
import axiosInstance from '@/plugins/axios';
import useAuthenticatedQuery from '@/composables/apiServices/useAuthenticatedQuery';
import { useDebounceFn } from '@vueuse/core';
import { computed } from 'vue';
import dayjs from 'dayjs';
import {
  clearThreadingShownStateBeforeUpdate,
  registerThreadingShownStateBeforeUpdate
} from '@/helpers/mailbox';

export const USER_SERVICE_UPDATE_DETAILS_MUTATION_KEY = ['updateUserDetails'];

let USER_ATTRIBUTES_UPDATE_CACHE: IAttributesUpdateRequest = {};
let USER_DETAILS_UPDATE_CACHE: IDetailsUpdateRequest = {};

/**
 * This service composable is used for all user related requests
 * All of these requests require the user to be authenticated, so they should use the userIndex
 * and queriesEnabled variables for proper request control
 */
const useUserService = () => {
  const queryClient = useQueryClient();
  const { userIndex, queriesEnabled } = useAuthenticatedQuery();

  const detailsQueryKey = computed(() => ['authenticatedQuery', userIndex, 'user', 'details']);
  const attributesQueryKey = computed(() => [
    'authenticatedQuery',
    userIndex,
    'user',
    'attributes'
  ]);

  const details = (): UseQueryReturnType<IDetailsResponse, DefaultError> => {
    return useQuery({
      queryKey: detailsQueryKey,
      queryFn: async ({ signal }) => {
        return (
          await axiosInstance.get(`/u/${userIndex.value}/details`, {
            signal
          })
        ).data;
      },
      enabled: queriesEnabled
    });
  };

  const updateDetails = () =>
    useMutation<
      void,
      DefaultError,
      IDetailsUpdateRequest,
      { previousResponse: IDetailsResponse | undefined }
    >({
      mutationKey: USER_SERVICE_UPDATE_DETAILS_MUTATION_KEY,
      mutationFn: useDebounceFn(async () => {
        const updateData = USER_DETAILS_UPDATE_CACHE;
        USER_DETAILS_UPDATE_CACHE = {};
        if (Object.keys(updateData).length === 0) {
          return;
        }
        await axiosInstance.patch(`/u/${userIndex.value}/details`, updateData);
      }, 5000),
      // When mutate is called:
      onMutate: async (newUpdateRequestData) => {
        // Cancel any outgoing re-fetches
        // (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries({ queryKey: detailsQueryKey.value });

        // Snapshot the previous value
        const previousResponse = queryClient.getQueryData<IDetailsResponse>(detailsQueryKey);

        // Optimistically update to the new value
        queryClient.setQueryData<IDetailsResponse>(
          detailsQueryKey,
          (oldDetails): IDetailsResponse | undefined => {
            if (!oldDetails) {
              return undefined;
            }

            const timezone = newUpdateRequestData.timezone ?? oldDetails.timezone;
            return {
              ...oldDetails,
              name: newUpdateRequestData.name ?? oldDetails.name,
              targets: newUpdateRequestData.targets ?? oldDetails.targets,
              spamLevel: newUpdateRequestData.spamLevel ?? oldDetails.spamLevel,
              timezone,
              view_mode: newUpdateRequestData.view_mode ?? oldDetails.view_mode,
              language: newUpdateRequestData.language ?? oldDetails.language,
              meta: {
                ...oldDetails.meta,
                browserTimezoneSet: !!timezone,
                onBoardingCompleted: String(
                  newUpdateRequestData.onBoardingCompleted ?? oldDetails.meta.onBoardingCompleted
                ),
                displayDensity:
                  newUpdateRequestData.displayDensity ?? oldDetails.meta.displayDensity,
                inboxType: newUpdateRequestData.inboxType ?? oldDetails.meta.inboxType,
                showContactList:
                  newUpdateRequestData.showContactList ?? oldDetails.meta.showContactList,
                ui: newUpdateRequestData.ui
                  ? {
                      color: newUpdateRequestData.ui.color,
                      theme: newUpdateRequestData.ui.theme,
                      useBrowserDark: false
                    }
                  : oldDetails.meta.ui,
                upgrade_request:
                  newUpdateRequestData.upgrade_request === null
                    ? null
                    : newUpdateRequestData.upgrade_request !== undefined
                      ? {
                          packageId: newUpdateRequestData.upgrade_request,
                          requestedAt: dayjs().format(),
                          current_uname: oldDetails.meta.uname ?? ''
                        }
                      : oldDetails.meta.upgrade_request,
                forward: newUpdateRequestData.forward
                  ? {
                      enabled: newUpdateRequestData.forward.enabled,
                      addressList:
                        newUpdateRequestData.targets ?? oldDetails.meta.forward?.addressList ?? []
                    }
                  : oldDetails.meta.forward
              }
            };
          }
        );

        USER_DETAILS_UPDATE_CACHE = {
          ...USER_DETAILS_UPDATE_CACHE,
          ...newUpdateRequestData
        };
        // Return a context object with the snapshotted value
        return { previousResponse };
      },
      // If the mutation fails,
      // use the context returned from onMutate to roll back
      onError: (_, __, context) => {
        if (context?.previousResponse) {
          queryClient.setQueryData<IDetailsResponse>(detailsQueryKey, context.previousResponse);
        } else {
          queryClient.invalidateQueries({ queryKey: detailsQueryKey.value });
        }
      }
      // Always refetch after error or success:
      // TODO: Figure out if we need to refetch or not because we have the event stream connection that will invalidate data anyways.
      // onSettled: () => queryClient.invalidateQueries({ queryKey: detailsQueryKey })
    });

  const attributes = (): UseQueryReturnType<IAttributesResponse, DefaultError> => {
    return useQuery({
      queryKey: attributesQueryKey,
      queryFn: async ({ signal }) => {
        return (
          await axiosInstance.get(`/u/${userIndex.value}/config`, {
            signal
          })
        ).data;
      },
      enabled: queriesEnabled
    });
  };

  const updateAttributes = () =>
    useMutation<
      void,
      DefaultError,
      IAttributesUpdateRequest,
      { previousResponse: IAttributesResponse | undefined }
    >({
      mutationKey: ['updateUserAttributes'],
      mutationFn: useDebounceFn(async () => {
        const updateData = USER_ATTRIBUTES_UPDATE_CACHE;
        USER_ATTRIBUTES_UPDATE_CACHE = {};
        if (Object.keys(updateData).length === 0) {
          return;
        }
        await axiosInstance.patch(`/u/${userIndex.value}/config`, updateData);
      }, 5000),
      // When mutate is called:
      onMutate: async (newUpdateRequestData) => {
        // Cancel any outgoing refetches
        // (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries({ queryKey: attributesQueryKey.value });

        // Snapshot the previous value
        const previousResponse = queryClient.getQueryData<IAttributesResponse>(attributesQueryKey);

        // Remember the value threadingShownState had before the optimistic update so that the
        // ATTRIBUTES_UPDATED event handler can detect a change initiated in this tab. Without
        // this, the optimistic update above would make the cache match the event payload and
        // the mailbox and search list queries would never be invalidated.
        if (
          newUpdateRequestData.threadingShownState !== undefined &&
          previousResponse?.threadingShownState !== undefined &&
          newUpdateRequestData.threadingShownState !== previousResponse.threadingShownState
        ) {
          registerThreadingShownStateBeforeUpdate(previousResponse.threadingShownState);
        }

        // Optimistically update to the new value
        queryClient.setQueryData<IAttributesResponse>(
          attributesQueryKey,
          (oldAttributes): IAttributesResponse | undefined => {
            if (!oldAttributes) {
              return undefined;
            }
            return {
              ...oldAttributes,
              ...newUpdateRequestData,
              panelSizes: newUpdateRequestData.panelSizes
                ? {
                    mailboxPanel:
                      newUpdateRequestData.panelSizes.mailboxPanel ??
                      oldAttributes.panelSizes?.mailboxPanel,
                    sidePanel:
                      newUpdateRequestData.panelSizes.sidePanel ??
                      oldAttributes.panelSizes?.sidePanel
                  }
                : oldAttributes.panelSizes
            };
          }
        );

        USER_ATTRIBUTES_UPDATE_CACHE = {
          ...USER_ATTRIBUTES_UPDATE_CACHE,
          ...newUpdateRequestData
        };

        return { previousResponse };
      },
      // If the mutation fails,
      // use the context returned from onMutate to roll back
      onError: (_, __, context) => {
        clearThreadingShownStateBeforeUpdate();
        if (context?.previousResponse) {
          queryClient.setQueryData<IAttributesResponse>(
            attributesQueryKey,
            context.previousResponse
          );
        } else {
          queryClient.invalidateQueries({ queryKey: attributesQueryKey.value });
        }
      }
      // Always refetch after error or success:
      // onSettled: () => queryClient.invalidateQueries({ queryKey: attributesQueryKey })
    });

  return {
    details,
    updateDetails,
    attributes,
    updateAttributes,
    attributesQueryKey,
    detailsQueryKey
  };
};

export default useUserService;
